import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchAllMedia, fetchBrochures, fetchDirectors, fetchSiteSettings, resolveMediaUrl } from "@/lib/api";
import { log } from "@/lib/logger";

const SiteContentContext = createContext(null);

// Default fallback images (used until admin uploads custom media).
// Visitors *always* see something beautiful; admin can override any of these via the media manager.
export const DEFAULT_MEDIA = {
  hero_home:
    "https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&w=2400&q=85",
  hero_about:
    "https://images.unsplash.com/photo-1554469384-e58fac16e23a?auto=format&fit=crop&w=2400&q=85",
  hero_projects:
    "https://images.unsplash.com/photo-1500916434205-0c77489c6cf7?auto=format&fit=crop&w=2400&q=85",
  hero_services:
    "https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=2400&q=85",
  hero_contact:
    "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=2400&q=85",
  featured_project:
    "https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=1800&q=85",
  service_1:
    "https://images.unsplash.com/photo-1487958449943-2429e8be8625?auto=format&fit=crop&w=1600&q=85",
  service_2:
    "https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=1600&q=85",
  service_3:
    "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1600&q=85",
  gallery_1:
    "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=900&q=80",
  gallery_2:
    "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=900&q=80",
  gallery_3:
    "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=900&q=80",
  gallery_4:
    "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=900&q=80",
  director_1: null,
  director_2: null,
  director_3: null,
  // Hotspot media (3D map popups) — optional admin overrides for image/audio per hotspot
  hotspot_entrance_image: null,
  hotspot_clubhouse_image: null,
  hotspot_temple_image: null,
  hotspot_playground_image: null,
  hotspot_amphitheater_image: null,
  hotspot_social_1_image: null,
  hotspot_social_2_image: null,
  hotspot_plots_image: null,
  hotspot_parks_image: null,
  hotspot_infrastructure_image: null,
  hotspot_entrance_audio: null,
  hotspot_clubhouse_audio: null,
  hotspot_temple_audio: null,
  hotspot_playground_audio: null,
  hotspot_amphitheater_audio: null,
  hotspot_social_1_audio: null,
  hotspot_social_2_audio: null,
  hotspot_plots_audio: null,
  hotspot_parks_audio: null,
  hotspot_infrastructure_audio: null,
};

export const ALL_MEDIA_KEYS = Object.keys(DEFAULT_MEDIA);

export const MEDIA_KEY_LABELS = {
  hero_home: "Home — Hero background",
  hero_about: "About — Hero background",
  hero_projects: "Projects — Hero background",
  hero_services: "Services — Hero background",
  hero_contact: "Contact — Hero background",
  featured_project: "Home — Featured project (Silva City)",
  service_1: "Services — Real Estate Development",
  service_2: "Services — Construction & Architecture",
  service_3: "Services — Asset Management & PropTech",
  gallery_1: "Projects — Gallery #1",
  gallery_2: "Projects — Gallery #2",
  gallery_3: "Projects — Gallery #3",
  gallery_4: "Projects — Gallery #4",
  director_1: "Directors — Photo #1",
  director_2: "Directors — Photo #2",
  director_3: "Directors — Photo #3",
  hotspot_entrance_image: "3D Map — Entrance image/GIF",
  hotspot_clubhouse_image: "3D Map — Clubhouse image/GIF",
  hotspot_temple_image: "3D Map — Temple image/GIF",
  hotspot_playground_image: "3D Map — Playground image/GIF",
  hotspot_amphitheater_image: "3D Map — Amphitheater image/GIF",
  hotspot_social_1_image: "3D Map — Social Infra 1 image/GIF",
  hotspot_social_2_image: "3D Map — Social Infra 2 image/GIF",
  hotspot_plots_image: "3D Map — Plots image/GIF",
  hotspot_parks_image: "3D Map — Parks image/GIF (legacy)",
  hotspot_infrastructure_image: "3D Map — Infrastructure image/GIF (legacy)",
  hotspot_entrance_audio: "3D Map — Entrance voiceover (MP3)",
  hotspot_clubhouse_audio: "3D Map — Clubhouse voiceover (MP3)",
  hotspot_temple_audio: "3D Map — Temple voiceover (MP3)",
  hotspot_playground_audio: "3D Map — Playground voiceover (MP3)",
  hotspot_amphitheater_audio: "3D Map — Amphitheater voiceover (MP3)",
  hotspot_social_1_audio: "3D Map — Social Infra 1 voiceover (MP3)",
  hotspot_social_2_audio: "3D Map — Social Infra 2 voiceover (MP3)",
  hotspot_plots_audio: "3D Map — Plots voiceover (MP3)",
  hotspot_parks_audio: "3D Map — Parks voiceover (MP3, legacy)",
  hotspot_infrastructure_audio: "3D Map — Infrastructure voiceover (MP3, legacy)",
};

export const SiteContentProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [mediaList, setMediaList] = useState([]); // raw list (all media items)
  const [mediaByKey, setMediaByKey] = useState({}); // key -> media item (most recent)
  const [directors, setDirectors] = useState([]);
  const [brochures, setBrochures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bootstrapped, setBootstrapped] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    // === Resilient bootstrap ===
    //
    // We use Promise.allSettled (not Promise.all) so that a transient failure
    // in ONE endpoint (e.g. a slow cold start, a CORS hiccup on the edge
    // proxy, a 500 from a single collection) does NOT wipe out the other
    // three. Previously a single rejected promise caused every setX call to
    // be skipped, leaving the site in a "no directors / no settings / no
    // media" state — which on production surfaced as a blank About page
    // (DirectorsSection returns null when directors=[]).
    //
    // Each fetch is now independent: whatever loads, renders. Whatever fails
    // is logged with full context and falls back to its empty default; the
    // section using it (e.g. DirectorsSection) handles the empty state on
    // its own.
    const [sRes, mRes, dRes, bRes] = await Promise.allSettled([
      fetchSiteSettings(),
      fetchAllMedia(),
      fetchDirectors(),
      fetchBrochures(),
    ]);

    if (sRes.status === "fulfilled") {
      setSettings(sRes.value);
    } else {
      log.error("[SiteContent] fetchSiteSettings failed:", sRes.reason?.message || sRes.reason);
    }

    if (mRes.status === "fulfilled") {
      const m = Array.isArray(mRes.value) ? mRes.value : [];
      setMediaList(m);
      const map = {};
      for (const item of m) {
        if (item.key && !map[item.key]) {
          map[item.key] = item; // first in sort-desc-by-createdAt = newest
        }
      }
      setMediaByKey(map);
    } else {
      log.error("[SiteContent] fetchAllMedia failed:", mRes.reason?.message || mRes.reason);
    }

    if (dRes.status === "fulfilled") {
      setDirectors(Array.isArray(dRes.value) ? dRes.value : []);
    } else {
      log.error("[SiteContent] fetchDirectors failed:", dRes.reason?.message || dRes.reason);
    }

    if (bRes.status === "fulfilled") {
      setBrochures(Array.isArray(bRes.value) ? bRes.value : []);
    } else {
      log.error("[SiteContent] fetchBrochures failed:", bRes.reason?.message || bRes.reason);
    }

    setBootstrapped(true);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ settings, mediaList, mediaByKey, directors, brochures, loading, bootstrapped, refresh }),
    [settings, mediaList, mediaByKey, directors, brochures, loading, bootstrapped, refresh]
  );

  return <SiteContentContext.Provider value={value}>{children}</SiteContentContext.Provider>;
};

export const useSiteContent = () => {
  const ctx = useContext(SiteContentContext);
  if (!ctx) {
    throw new Error("useSiteContent must be used within SiteContentProvider");
  }
  return ctx;
};

// Convenience hooks
export const useSettings = () => {
  const { settings } = useSiteContent();
  return settings;
};

export const useDirectors = () => {
  const { directors } = useSiteContent();
  return directors;
};

export const useBrochures = () => {
  const { brochures } = useSiteContent();
  return brochures;
};

/**
 * Returns the URL for the media at `key`, falling back to the default stock image.
 * Always returns an absolute URL so it can be passed directly to <img src>.
 */
export const useMedia = (key) => {
  const { mediaByKey } = useSiteContent();
  const item = mediaByKey?.[key];
  if (item?.url) {
    return resolveMediaUrl(item.url);
  }
  return DEFAULT_MEDIA[key] || null;
};

/** Same as useMedia but returns the full media item (or null if only default). */
export const useMediaItem = (key) => {
  const { mediaByKey } = useSiteContent();
  return mediaByKey?.[key] || null;
};
